/* eslint-disable @typescript-eslint/no-use-before-define */
import { createKeys, encode64, pbkdf2, PrivateKey, randomBytes, SHA, SignedRecord } from "universa-minicrypto";
import { Endpoint, ErrorCode, IConnection, RemoteException, RootConnection } from "@/sergecych.uni3/Parsec";
import { Logger } from "@/sergecych.uni3/Logger";
import {
  SerializedUniversalKey,
  UniversalKey,
  UniversalKeys,
  UniversalPasswordKey,
  UniversalPrivateKey,
  UniversalSymmetricKey
} from "@/sergecych.uni3/UnversalKeys";
import { Coffer } from "@/sergecych.uni3/Coffer";
import { longToByteArray, retry, timeout, utf8ToBytes } from "@/sergecych.uni3/tools";
import { bossDump, bossLoad } from "@/sergecych.uni3/SimpleBoss";
import Password from "@/components/Password.vue";
import { Passwords } from "@/sergecych.uni3/Passwords";
import { TimeService } from "@/sergecych.uni3/TimeService";


const LS_ENDPOINT_KEY = "myoservice.endpoint";
const CLOUD_REGISTRY_KEY = "myoservice.registry";

interface SerilizedCloudRegistry {
  cloudKey: SerializedUniversalKey;
  version: number;
  itemTags: {
    note: string
  }
}

function error(code: string, text: string): never {
  throw new ErrorCode(code, text);
}

/**
 * The registry is a special unique-tagged object which holds global settings for the storage,
 * including its internal storage key, `cloudKey` and some tag definitions.
 */
class MyoRegistry {
  #coffer: Coffer | undefined;
  #connection: IConnection;
  readonly #myoLogin: MyoLogin;
  #serialized: SerilizedCloudRegistry | undefined;
  #cloudKey: UniversalKey | undefined;

  constructor(connection: IConnection, myoLogin: MyoLogin) {
    this.#myoLogin = myoLogin;
    this.#connection = connection;
  }

  private async load(): Promise<boolean> {
    console.log("loading registry...");
    const result = await this.#connection.call("getByUniqueTag", { uniqueTag: CLOUD_REGISTRY_KEY });
    if (result.element) {
      console.log("found registry object:", result);
      this.#coffer = Coffer.unpack(result.element.head, this.#myoLogin.password)
      this.#serialized = this.#coffer.payloadObject as SerilizedCloudRegistry;
      console.log("registry unpacked", this.#serialized);
      const serializedKey = this.#serialized.cloudKey;
      if (serializedKey) {
        this.#cloudKey = UniversalKeys.loadFrom(serializedKey);
        return true;
      }
      console.warn("serialized registry does not contain cloud key:", this.#serialized)
    } else
      console.debug("missing registry object")
    return false;
  }

  private async create() {
    console.debug("creating new one");
    const sk = this.#myoLogin.storageKey;
    if (!sk) throw "storage access key not present in login, can't create registry, setup failed";
    const cloudKey = UniversalSymmetricKey.createRandom();
    this.#serialized = {
      cloudKey: cloudKey.serialize(),
      version: 1,
      itemTags: { note: Passwords.randomId(16) }
    };
    this.#coffer = Coffer.create(sk).setPayload(this.#serialized)
    const result = await this.#connection.call("setByUniqueTag", {
      uniqueTag: CLOUD_REGISTRY_KEY, revision: 0, head: this.#coffer.pack()
    });
    console.log("new registry created");
    return this;
  }

  get cloudKey(): UniversalKey {
    return this.#cloudKey ?? error("registry_not_initialized", "initialize the retistry");
  }

  get tags(): any {
    return { ...this.#serialized?.itemTags ?? error("registry_not_initialized", "initialize the retistry") }
  }

  /**
   * Load existing registry or create new ones. It perform several attempts in the case the
   * network failure.
   */
  async loadOrCreate() {
    await retry(10, 2000, async () => {
      if (!await this.load()) {
        await this.create();
        await this.load();
      }
    });
  }
}

export class MyoService extends Logger implements IConnection {
  static readonly connection = new RootConnection("https://api.myonly.cloud/api/p0");
  // static readonly connection = new RootConnection(
  //   window.location.origin.startsWith("http://localhost") ? "http://localhost:9090/api/p0" : "https://api.myonly.cloud/api/p0");
  #myoLogin: MyoLogin;
  #endpoint?: Endpoint;
  #_isConnected = false;
  #registry: MyoRegistry;

  /**
   * Construct service from partial arguments. Login is always needed, and enpoint could be
   * pesent if, say, was saved in a session. Endpoint could be bad or missing, service will
   * recreate it from the login. If login fails due to credential problems, service instance will
   * become invalid and application should recreate it iwth a new login^ for example,
   * by asking user to re-enter the password.
   *
   * @param myoLogin
   * @param currentEndpoint
   */
  constructor(myoLogin: MyoLogin, currentEndpoint?: Endpoint) {
    super("MYOS");
    this.#myoLogin = myoLogin;
    this.#endpoint = currentEndpoint;
    if (!this.#endpoint)
      this.restoreEndpoint();
    this.#registry = new MyoRegistry(this, myoLogin);
  }

  /**
   * Call it after construction to ensure all necessary structures are created. If it does not rejects,
   * service is ready.
   */
  async setup(): Promise<MyoService> {
    await this.#registry.loadOrCreate();
    return this;
  }

  get registry(): MyoRegistry { return this.#registry }

  get cloudKey(): UniversalSymmetricKey {
    return this.registry.cloudKey as UniversalSymmetricKey;
  }

  createCoffer(): Coffer {
    if( !this.cloudKey )
      throw "service is not properly initialized";
    return Coffer.create(this.cloudKey)
  }

  /**
   * Automatically manages endpoint resoration using login instance, then executes a command
   * over the restored endpoint. Manages endpoint becoming invalid.
   *
   * @param method method to invoke
   * @param params any params to it
   */
  async call(method: string, params: any = {}): Promise<any> {
    let attempts = 0;
    while (!this.#endpoint && attempts < 10) {
      if (!this.#endpoint) {
        this.log("restoring endpoint from login");
        try {
          const ep = await this.#myoLogin.perform();
          const test = await ep.call("check")
          this.#endpoint = ep;
          this.saveEndpoint();
        } catch (e) {
          console.error("error while re-logging in", e);
          attempts++;
        }
      }
    }
    if (!this.#endpoint) {
      this.#_isConnected = false;
      throw "disconnected";
    }
    return this.#endpoint.call(method, params);
  }

  /**
   * Save or clear endpoint in the localStorage
   */
  private saveEndpoint() {
    if (this.#endpoint)
      localStorage.setItem(LS_ENDPOINT_KEY, encode64(bossDump(this.#endpoint.serialize())));
    else
      localStorage.removeItem(LS_ENDPOINT_KEY)
  }

  /**
   * Try to restore and check the endpoint from the local storage
   */
  private restoreEndpoint() {
    try {
      const paramsString = localStorage.getItem(LS_ENDPOINT_KEY);
      // if (paramsString) {
      //   this.debug("found saved endpoint in LS");
      //   this.#endpoint = new Endpoint(this.#myoLogin.connection, bossLoad(paramsString))
      // }
    } catch (e) {
      this.debug("failed to restore endpoint", e);
    }
  }

  static async signIn(login: string,password: string): Promise<MyoService> {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new MyoService(new MyoLogin({login, password, connection: this.connection})).setup()
  }

  async signOut() {
    if( this.#endpoint )
      await this.#endpoint.call("logout");
    this.#endpoint = undefined;
    this.saveEndpoint();
  }

  async check(): Promise<any> {
    return await this.call("ping");
  }

  static async generateKey(keySize = 4096): Promise<PrivateKey> {
    throw "obsoleted"
  }

  static async test() {
    const result = await MyoService.connection.call("info");
    console.debug("service info: " + JSON.stringify(result));
    return result;
  }

  async register(l: string, p: PrivateKey, e: Uint8Array) {
    throw "obsoleted";
  }

  tryRestoreLogin() {
    throw "obsoleted";
  }

  async loginWithKey(privateKey: PrivateKey, packedPrivateKey?: Uint8Array) {
    throw "obsoleted";
  }

  get isLoggedIn() {
    return false;
    // return this.connection.isLoggedIn;
  }

  private readonly ls = window.localStorage;
  private readonly ss = window.sessionStorage;

  static get instance(): MyoService | undefined {
    return undefined;
  }

  /**
   * Create new registration object. It immediately starts key generation in a worker thread.
   * see {@linkcode MyoRegistration} for details.
   *
   * @param keyLength
   */
  static createRegistration(keyLength: 2048 | 4096 | 8192 = 8192): MyoRegistration {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new MyoRegistration(this.connection, keyLength)
  }

  static loginHashFrom(login: string) {
    const loginHash = encode64(pbkdf2(new SHA("256"), {
      rounds: 1000, // number of iterations
      keyLength: 10, // bytes length
      password: login,
      salt: utf8ToBytes("myonlycloud_login_hash")
    }));
    return loginHash;
  }
}

/**
 * Slow registration procedure carrier. Performs by steps to minimize user inactiviry
 *
 * 1. Upon construction, immediately start generating new private key ina worker thread.
 *
 * 2. When the password is known, start generating derived keys for the system
 *
 * 3. {{performWithLogin}} awaits 1 and 2 (password must be set or it will throw)
 *    and perform actual registration.
 *    the succeess.
 */
export class MyoRegistration {
  #timeService: TimeService | undefined;
  readonly #privateKey: Promise<UniversalPrivateKey>;
  #loginKey ?: UniversalPasswordKey;
  #storageKey ?: UniversalPasswordKey;
  private passwordHint?: string;
  #logger = new Logger("MYR")
  #password: string | undefined;
  readonly connection: RootConnection;

  constructor(connection: RootConnection, keyLength: 2048 | 4096 | 8192) {
    this.#privateKey = UniversalPrivateKey.generate(keyLength);
    this.connection = connection;
  }

  /**
   * Slow, blocking syn password derivarion method. It takes time...
   * @param password
   */
  setPassword(password: string, rounds = 50000, hint?: string) {
    const kk = MyoRegistration.deriveKeys(password, rounds);
    [this.#loginKey, this.#storageKey] = [kk.loginKey, kk.storageKey]
    this.#password = password;
    this.passwordHint = hint;
    // if we get so far we will ned time service.
    if( !this.#timeService )
      this.#timeService = new TimeService(this.connection.rootUri);
  }

  static deriveKeys(password: string, rounds = 50000): { loginKey: UniversalPasswordKey, storageKey: UniversalPasswordKey } {
    const keys = UniversalPasswordKey.deriveMiltiple(password, 2, { rounds })
    return { loginKey: keys[0], storageKey: keys[1] }
  }

  /**
   * Attempt to register new account with a given login. If the sevice answer with RemoteError with code
   * `login_not_available`, application should ask user to select another login instead. Code `expired` means
   * clock problem or network too busy and could be retired with the same login.
   *
   * @param login login name to use
   */
  async performWithLogin(login: string): Promise<MyoService> {
    if (!this.#loginKey || !this.#storageKey)
      throw "password is not set";
    if (login.length < 3) throw "login is too short";
    if (login[0] == "." || login[0] == "_") throw "login must not start with '_' or '.'";
    if (login.indexOf(' ') >= 0) throw "login must not contain spaces";
    const loginHash = MyoService.loginHashFrom(login);
    const key = await this.#privateKey

    console.log("key stiff1:", key);
    console.log(key.serialize());
    console.log("key stiff2:", this.#loginKey);
    console.log(this.#loginKey!.serialize());
    console.log("coffer stuff:");
    const cc = Coffer.create(this.#loginKey!).setPayload({ loginKey: key.serialize() });
    console.log(cc);
    console.log(cc.serialize());
    console.log("continue....");

    if( !this.#timeService ) throw("logic error: #timeServce must be already initialized");
    const nonce = longToByteArray(await this.#timeService.remoteEpochSecond());
    const sr = SignedRecord.packWithKey(key.privateKey,
      {
        loginHash: loginHash,
        encryptedKey: Coffer.create(this.#loginKey!).setPayload({ loginKey: key.serialize() }).pack()
      },
      nonce
    );
    try {
      const res = await this.connection.call(
        "register", {
          signedRecord: sr
        }
      )
    } catch (e) {
      if (e instanceof RemoteException && e.code == 'key_is_registered')
        this.#logger.log("key is already registered (with the same login)");
      else
        throw e;
    }
    // at this point we should be able to login
    return await new MyoService(
      new MyoLogin({
        login, loginHash,
        connection: this.connection,
        password: this.#password!,
        storageKey: this.#storageKey
      })).setup();
  }
}

interface LoginConfig {
  connection: IConnection,
  login?: string,
  loginHash?: string,
  password: string,
  loginKey?: UniversalKey,
  storageKey?: UniversalKey
}


class MyoLogin {
  readonly connection: IConnection;
  readonly cfg: LoginConfig;
  readonly password: string;

  constructor(cfg: LoginConfig) {
    this.cfg = cfg;
    if( !cfg.loginHash )
      cfg.loginHash = MyoService.loginHashFrom(cfg.login!);
    this.password = cfg.password;
    this.connection = cfg.connection;
  }

  get storageKey(): UniversalKey | undefined {
    if (!this.cfg.storageKey)
      console.warn("requested missing storage key, login config is:", this.cfg);
    return this.cfg.storageKey
  }

  async perform(): Promise<Endpoint> {
    const conn = this.connection;

    // we use login to load encrypted access key
    let result = await conn.call("requestLogin", { loginHash: this.cfg.loginHash });

    // password will not re-rerevied here if it already was derived (e.g. when registering)
    const coffer = Coffer.unpack(result.encryptedKey, this.password);
    const serialized = coffer.payloadObject?.loginKey
    if (!serialized) throw new RemoteException("illegal encrypted access key, can't access");
    const accessKey = UniversalKeys.loadFrom(serialized);
    if (!(accessKey instanceof UniversalPrivateKey))
      throw new RemoteException("illegal access key: not a private key, can't access")

    // we have decrypted access key, so we can finish the login:
    const sr = SignedRecord.packWithKey(
      accessKey.privateKey, { loginToken: result.loginToken }, utf8ToBytes(result.nonce)
    );
    result = await conn.call("login", { signedRecord: sr });
    const endpoint = new Endpoint(this.connection, bossLoad(accessKey.privateKey.decrypt(result.loginData)));
    return endpoint;
  }

}


